// Reductions folder builder (Stage 6 — TASKS-UI.md).
//
// One reduction per domain. The domain strip (shared with Volume/Contours)
// doubles as the reduction selector: the active tile names which reduction the
// controls above edit. There is no list and no Add button — domains *are* the
// reductions.
//
// Layout (top → bottom), mirroring Volume/Contours:
//
//   Field   |B| ▾
//   Op      mean ▾
//   Reduce x ✓   y ✓   z ☐      (z off → 1-D profile along z)
//   Timestep Follow ▾
//   ───
//   <result chip — scalar value (0-D) or "1-D along z · …" (profile)>
//   [2-D heatmap — coming in v2]
//   ───
//   [ Plot ] [ Reset ]          ← .pane-actions tiles
//   DOMAIN │ [Full][Box 1]…     ← shared domain strip; selects the reduction
//
// The footer DOM (action tiles + domain strip) is raw DOM appended to the
// folder body, cached on the folder so it survives pane child disposal.
// The result chip re-subscribes to the cache each rebuild (active id changes).

import { store, params as storeParams } from '@store/instance';
import { INTENTS } from '@store/intents';
import { setDomain } from '@store/selections';
import { reductionForDomain } from '@store/reductions';
import { buildActionTiles } from './actionTiles';
import { buildDomainStrip, renderDomainStrip, syncDomainSelected, type DomainStripRefs } from './domainStrip';
import type { TPaneLike, TFolderLike, Refs } from '../types';
import type { Reduction, ReductionId, SelectionId } from '@store/schema';
import type { FieldMetadata } from '@physics/types';
import { SUPPORTED_OPS } from '../../../reductions/types';
import { formatReductionResult } from '../../../reductions/format';
import { getResult, onResult } from '../../../reductions/cache';

interface ReductionsCtx {
    fieldMeta: FieldMetadata | null;
    openPlot: (id: ReductionId) => void;
}

interface ReductionsCustomRefs {
    actions: HTMLDivElement;
    actionsRow: HTMLDivElement;
    axisRow: HTMLDivElement;
    sepFooter: HTMLDivElement;
    resultBlock: HTMLDivElement;
    domain: DomainStripRefs;
    offRefresh: () => void;
    offResult: () => void;
}

const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, ReductionsCustomRefs>();

const ALL_OPS = ['integrate', 'sum', 'mean', 'median', 'max', 'min', 'std', 'var', 'argmax', 'argmin'] as const;

function disposeFolderChildren(folder: TFolderLike): void {
    const children = Array.isArray(folder.children) ? folder.children.slice() : [];
    for (const child of children) (child as TFolderLike).dispose?.();
}

function fieldOptions(meta: FieldMetadata | null): Record<string, string> {
    const options: Record<string, string> = {};
    const comps = meta && Array.isArray(meta.field_components) ? meta.field_components : null;
    if (comps && comps.includes('Bx') && comps.includes('By') && comps.includes('Bz')) {
        options['|B|'] = 'magnitude';
    }
    if (comps) for (const c of comps) options[c] = c;
    if (Object.keys(options).length === 0) options['|B|'] = 'magnitude';
    return options;
}

function opOptions(): Record<string, string> {
    // pane selects can't disable individual options; suffix the unsupported
    // ops with "(v2)" so users see they exist but aren't wired yet. Picking one
    // results in `kind: 'unsupported'` from the compute dispatcher, surfaced
    // in the result chip text.
    const out: Record<string, string> = {};
    for (const op of SUPPORTED_OPS) out[op] = op;
    for (const op of ALL_OPS) {
        if ((SUPPORTED_OPS as readonly string[]).includes(op)) continue;
        out[`${op} (v2)`] = op;
    }
    return out;
}

/** The reduction bound to the active domain tile. One-per-domain → at most one. */
function activeReductionId(): ReductionId | null {
    return reductionForDomain(String(storeParams.reductionsDomain ?? 'full'));
}

/** Domain-strip click: switch the active reduction to `id`'s reduction,
 *  lazily creating one when this domain hasn't been touched yet. */
function activateReductionDomain(id: SelectionId | 'full'): void {
    setDomain('reductions', id);
    const existing = reductionForDomain(id);
    if (existing) {
        if (storeParams.focusedReductionId !== existing) {
            store.dispatch(INTENTS.REDUCTION_FOCUS, { id: existing });
        }
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
    } else {
        // The create handler computes the result and re-dispatches the rebuild.
        store.dispatch(INTENTS.REDUCTION_CREATE, { seed: { domain: id }, focus: true });
    }
}

const AXES = ['x', 'y', 'z'] as const;

/** Build the "Reduce in" row: a label plus three square on/off axis toggles
 *  (shared `.pane-action` visual language). Click handlers resolve the active
 *  reduction at click time so the cached row never holds a stale id. */
function buildAxisToggleRow(): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'reduce-axes-row';

    const label = document.createElement('span');
    label.className = 'reduce-axes-label';
    label.textContent = 'Reduce in';

    const tiles = document.createElement('div');
    tiles.className = 'reduce-axes-tiles';
    for (const axis of AXES) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'axis-toggle';
        btn.dataset['axis'] = axis;
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', `Reduce in ${axis}`);
        btn.title = `Toggle reduction along ${axis}`;
        // Letter glyph + a rail-style glow dot that lights when the axis is on
        // (mirrors the Volume domain tiles' `.domain-strip-tab-light`).
        btn.innerHTML = `<span class="axis-toggle-glyph">${axis}</span>`
            + `<span class="axis-toggle-light" aria-hidden="true"></span>`;
        // JS-driven hover (WebKit reverse-repaint workaround — see actionTiles.ts).
        btn.addEventListener('pointerenter', () => { btn.classList.add('is-hover'); });
        btn.addEventListener('pointerleave', () => { btn.classList.remove('is-hover'); });
        btn.addEventListener('pointercancel', () => { btn.classList.remove('is-hover'); });
        btn.addEventListener('click', () => {
            btn.classList.remove('is-hover');
            btn.blur();
            const aid = activeReductionId();
            if (!aid) return;
            const cur = storeParams.reductions[aid];
            if (!cur) return;
            // Toggle this axis; keep canonical x→y→z order. (Reducing over zero
            // axes is a no-op, so the store normalises an empty set back to all.)
            const next = AXES.filter((a) => (a === axis ? !cur.axes.includes(a) : cur.axes.includes(a)));
            store.dispatch(INTENTS.REDUCTION_UPDATE, { id: aid, patch: { axes: next } });
            syncAxisToggles(row);
        });
        tiles.append(btn);
    }

    row.append(label, tiles);
    return row;
}

/** Reflect the active reduction's axes onto the toggle row's pressed state. */
function syncAxisToggles(row: HTMLDivElement): void {
    const aid = activeReductionId();
    const r = aid ? storeParams.reductions[aid] : undefined;
    row.querySelectorAll<HTMLButtonElement>('.axis-toggle').forEach((btn) => {
        const axis = btn.dataset['axis'];
        const on = !!(r && axis && r.axes.includes(axis as 'x' | 'y' | 'z'));
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-pressed', String(on));
    });
}

/** A 1-px hairline divider matching the pane's pane separators
 *  (`.mz-sprv_r`). Raw DOM so its position relative to the injected rows is
 *  deterministic — an `addBlade` separator groups with the blades and
 *  lands above the injected "Reduce in" row instead of after it. */
function buildSep(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'reduction-sep';
    return el;
}

/** Two-line result block: a one-line lineage caption (metadata) over the
 *  formatted result (bold headline + muted auxiliary). Cached raw DOM, filled
 *  by renderResultBlock; the result CSS predates the (now-removed) Inspector. */
function buildResultBlock(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'reduction-result';
    const lineage = document.createElement('div');
    lineage.className = 'reduction-result-lineage';
    const body = document.createElement('div');
    body.className = 'reduction-result-body';
    const main = document.createElement('div');
    main.className = 'reduction-result-main';
    const aux = document.createElement('div');
    aux.className = 'reduction-result-aux';
    body.append(main, aux);
    wrap.append(lineage, body);
    return wrap;
}

/** One-line lineage caption (e.g. `mean(|B|) along z · Box 1`). */
function reductionLineage(r: Reduction): string {
    const surviving = (['x', 'y', 'z'] as const).filter((a) => !r.axes.includes(a));
    const along = surviving.length ? ` along ${surviving.join(',')}` : ' (0-D)';
    const domain = r.domain === 'full'
        ? 'Full'
        : storeParams.selections[r.domain]?.label ?? '(deleted)';
    return `${r.op}(${r.field})${along} · ${domain}`;
}

/** Fill the cached result block from the active reduction's spec + cached result. */
function renderResultBlock(block: HTMLDivElement, id: ReductionId | null): void {
    const lineage = block.querySelector<HTMLElement>('.reduction-result-lineage');
    const main = block.querySelector<HTMLElement>('.reduction-result-main');
    const aux = block.querySelector<HTMLElement>('.reduction-result-aux');
    if (!lineage || !main || !aux) return;
    const r = id ? storeParams.reductions[id] : undefined;
    if (!id || !r) {
        lineage.textContent = '';
        main.textContent = '—';
        aux.textContent = '';
        return;
    }
    lineage.textContent = reductionLineage(r);
    const { main: m, aux: a } = formatReductionResult(getResult(id));
    main.textContent = m;
    aux.textContent = a;
}

/** Bind the pane control rows (Field, Op, Timestep). Returns the Timestep
 *  blade element so the caller can slot the axis-toggle row right after it. */
function addReductionControls(folder: TFolderLike, id: ReductionId, r: Reduction, ctx: ReductionsCtx): HTMLElement | null {
    const edit = {
        field: r.field,
        op: r.op as string,
        timestep: r.timestepPolicy as string,
    };

    folder.addBinding(edit, 'field', { label: 'Field', options: fieldOptions(ctx.fieldMeta) }).on('change', (ev) => {
        store.dispatch(INTENTS.REDUCTION_UPDATE, { id, patch: { field: String(ev.value) } });
    });
    folder.addBinding(edit, 'op', { label: 'Operation', options: opOptions() }).on('change', (ev) => {
        store.dispatch(INTENTS.REDUCTION_UPDATE, { id, patch: { op: ev.value as Reduction['op'] } });
    });
    const tsBinding = folder.addBinding(edit, 'timestep', { label: 'Timestep', options: { 'Pin (t=N)': 'pin', 'Follow scrubber': 'follow' } });
    tsBinding.on('change', (ev) => {
        store.dispatch(INTENTS.REDUCTION_UPDATE, { id, patch: { timestepPolicy: ev.value === 'pin' ? 'pin' : 'follow' } });
    });

    return tsBinding.element ?? null;
}

export function buildReductionsSection(paneArg: unknown, refs: Refs, ctx: ReductionsCtx): Refs {
    const pane = paneArg as TPaneLike;
    let folder = refs['reductionsFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Reductions', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'reductions';
        refs['reductionsFolderRef'] = folder;
    } else {
        disposeFolderChildren(folder);
    }

    // Drop the prior build's per-build subscribers; the footer DOM is preserved.
    const existingRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (existingRefs) {
        try { existingRefs.offRefresh(); } catch { /* swallow */ }
        try { existingRefs.offResult(); } catch { /* swallow */ }
    }

    // Axis row, the footer hairline, and the result block are raw DOM, cached
    // like the footer so they survive pane child disposal. Detached
    // up-front; re-inserted in order below when there's a reduction to edit.
    const axisRow = existingRefs?.axisRow ?? buildAxisToggleRow();
    const sepFooter = existingRefs?.sepFooter ?? buildSep();
    const resultBlock = existingRefs?.resultBlock ?? buildResultBlock();
    for (const el of [axisRow, sepFooter, resultBlock]) el.remove();

    const activeDomain = String(storeParams.reductionsDomain ?? 'full');
    const id = reductionForDomain(activeDomain);
    const r = id ? storeParams.reductions[id] : undefined;

    // Controls exist only when the active domain has a reduction. The Full
    // domain is seeded at dataset load (see app/intents/reductions.ts), so this
    // placeholder only shows before any data is present. New selection tiles
    // create their reduction on click (activateReductionDomain), so the pane
    // never creates-by-dispatch here — that would race the initial build.
    if (id && r) {
        // Keep focusedReductionId tracking the active tile (palette focus commands).
        if (storeParams.focusedReductionId !== id) {
            store.dispatch(INTENTS.REDUCTION_FOCUS, { id });
        }

        const timestepEl = addReductionControls(folder, id, r, ctx);
        // Slot the "Reduce in" toggle row right after the Timestep blade.
        if (timestepEl) timestepEl.insertAdjacentElement('afterend', axisRow);
        syncAxisToggles(axisRow);
    } else {
        folder.addButton?.({ title: 'No data yet — load a dataset to reduce', disabled: true });
    }

    // ── Footer DOM: action tiles + domain strip. Cached on the folder so they
    // survive pane child disposal; re-pinned to the body bottom each build.
    const folderBodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (folderBodyEl) {
        folderBodyEl.querySelectorAll<HTMLElement>('.reduction-actions').forEach((orphan) => {
            if (orphan !== existingRefs?.actionsRow) orphan.remove();
        });
        folderBodyEl.querySelectorAll<HTMLElement>('.reduce-axes-row').forEach((orphan) => {
            if (orphan !== axisRow) orphan.remove();
        });
        folderBodyEl.querySelectorAll<HTMLElement>('.reduction-sep').forEach((orphan) => {
            if (orphan !== sepFooter) orphan.remove();
        });
        folderBodyEl.querySelectorAll<HTMLElement>('.reduction-result').forEach((orphan) => {
            if (orphan !== resultBlock) orphan.remove();
        });
        folderBodyEl.querySelectorAll<HTMLElement>('.domain-strip').forEach((orphan) => {
            if (orphan !== existingRefs?.domain.container) orphan.remove();
        });
    }

    // Plot/Reset resolve the active id at click time (the cached closures must
    // not capture a stale id across domain switches).
    const actions = existingRefs?.actions ?? buildActionTiles([
        { icon: 'fa-chart-line', label: 'Plot', onClick: () => { const aid = activeReductionId(); if (aid) ctx.openPlot(aid); } },
        {
            cls: 'is-clear', icon: 'fa-undo', label: 'Reset', onClick: () => {
                const aid = activeReductionId();
                if (!aid) return;
                store.dispatch(INTENTS.REDUCTION_UPDATE, { id: aid, patch: { field: 'magnitude', op: 'mean', axes: ['x', 'y', 'z'], timestepPolicy: 'follow' } });
                store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'reductions' });
            },
        },
    ]);
    // Wrap the tiles with a vertical "ACTIONS" side label (mirrors the domain row).
    const actionsRow = existingRefs?.actionsRow ?? (() => {
        const row = document.createElement('div');
        row.className = 'reduction-actions pane-side-row';
        const side = document.createElement('div');
        side.className = 'pane-side-label';
        side.setAttribute('aria-hidden', 'true');
        side.textContent = 'ACTIONS';
        row.append(side, actions);
        return row;
    })();

    let domain = existingRefs?.domain;
    if (!domain) {
        domain = buildDomainStrip('reductions', { onTileClick: activateReductionDomain });
    } else {
        renderDomainStrip(domain.stripEl, 'reductions');
    }

    const offRefresh = store.onIntent(INTENTS.UI_PANE_REFRESH, () => {
        syncDomainSelected('reductions');
        syncAxisToggles(axisRow);
    });

    // Result block tracks the active domain's reduction. Field/op/axes edits
    // dispatch REDUCTION_UPDATE → recompute → setResult → onResult, so both
    // lines stay fresh without a pane rebuild; a domain switch rebuilds and
    // re-subscribes to the new id here.
    renderResultBlock(resultBlock, id);
    const offResult = id
        ? onResult(id, () => renderResultBlock(resultBlock, id))
        : (): void => { /* no active reduction */ };

    CUSTOM_REFS_BY_FOLDER.set(folder, { actions, actionsRow, axisRow, sepFooter, resultBlock, domain, offRefresh, offResult });

    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (bodyEl) {
        bodyEl.appendChild(sepFooter);
        bodyEl.appendChild(resultBlock);
        bodyEl.appendChild(actionsRow);
        bodyEl.appendChild(domain.container);
    }

    if (pane && pane.refresh) pane.refresh();
    return refs;
}
